import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";

/**
 * Wraps a form-submit axios call to detect HTTP 409 duplicate responses.
 * On 409, opens a confirmation dialog with three actions:
 *   - "Utiliser celui-ci" on a match → resolves submit() with { usedExisting: match }
 *   - "Créer un nouveau" → retries with force=true and resolves with new entity data
 *   - "Annuler" → resolves with undefined
 *
 * Usage:
 *   const { submit, dialogProps } = useDuplicateConfirm(async (force) =>
 *     axios.post(`${API}/opportunities${force ? "?force=true" : ""}`, payload)
 *   );
 *   const result = await submit();
 *   if (result?.usedExisting) { ...use the existing match... }
 *   else if (result) { ...handle newly created... }
 *   <DuplicateConfirmDialog {...dialogProps} />
 */
const useDuplicateConfirm = (sender) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const resolveRef = useRef(null);

  const run = useCallback(async (force) => {
    try {
      const res = await sender(force);
      return res?.data ?? res;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "duplicate") {
        setInfo(detail);
        setOpen(true);
        return new Promise((resolve) => {
          resolveRef.current = resolve;
        });
      }
      const msg = typeof detail === "string" ? detail : detail?.message || "Erreur création";
      toast.error(msg);
      throw e;
    }
  }, [sender]);

  const submit = useCallback(() => run(false), [run]);

  const onConfirm = useCallback(async () => {
    // Claim the resolver SYNCHRONOUSLY so onOpenChange(false) (called right
    // after by the dialog button) cannot auto-cancel us with `undefined`.
    const resolve = resolveRef.current;
    resolveRef.current = null;
    if (!resolve) return;
    try {
      const res = await sender(true);
      const data = res?.data ?? res;
      resolve(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Erreur création";
      toast.error(msg);
      resolve(undefined);
    }
  }, [sender]);

  const onUseExisting = useCallback((match) => {
    // Same sync claim — defends against onOpenChange(false) firing right after.
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.({ usedExisting: match });
  }, []);

  const onOpenChange = useCallback((next) => {
    setOpen(next);
    if (!next) {
      // Dialog dismissed (Annuler) → resolve with undefined if not already resolved
      resolveRef.current?.(undefined);
      resolveRef.current = null;
    }
  }, []);

  return {
    submit,
    dialogProps: { open, onOpenChange, info, onConfirm, onUseExisting },
  };
};

export default useDuplicateConfirm;
